import React, { useState, useEffect } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Loader2, Shield, Mail, Lock, AlertCircle, Eye, EyeOff } from 'lucide-react'
import { login, AdminUser } from '@/lib/authService'

interface LoginProps {
  onLogin: (user: AdminUser) => void
  on2FARequired: (twoFactorToken: string, user: Partial<AdminUser>) => void
}

export default function LoginNew({ onLogin, on2FARequired }: LoginProps) {
  console.log('🔵 [DEBUG] LoginNew component rendered - USING NEW LOGIN COMPONENT')

  useEffect(() => {
    document.title = 'Login - FaithYoke'
  }, [])

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [rememberMe, setRememberMe] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [resetMode, setResetMode] = useState(false)
  const [resetSent, setResetSent] = useState(false)

  async function handleLogin(e: React.FormEvent) {
    e.preventDefault()
    setError('')

    console.log('🎯 [LOGINPAGE] Form submitted, email:', email)

    if (!email || !password) {
      setError('Please enter both email and password')
      return
    }

    try {
      setLoading(true)
      console.log('🎯 [LOGINPAGE] Calling login service...')

      const result = await login({ email, password, rememberMe })

      console.log('🎯 [LOGINPAGE] Login result:', {
        success: result.success,
        requiresTwoFactor: result.requiresTwoFactor,
        hasUser: !!result.user,
        error: result.error
      })

      if (!result.success) {
        console.log('🎯 [LOGINPAGE] Login failed:', result.error)
        setError(result.error || 'Login failed')
        return
      }

      if (result.requiresTwoFactor && result.twoFactorToken) {
        console.log('🎯 [LOGINPAGE] 2FA required, calling on2FARequired...')
        // Redirect to 2FA page
        on2FARequired(result.twoFactorToken, { email })
        return
      }

      if (result.user) {
        console.log('🎯 [LOGINPAGE] Login successful! Calling onLogin callback...')
        // Call onLogin callback (session is handled by Supabase)
        onLogin(result.user)
        console.log('🎯 [LOGINPAGE] onLogin callback completed')
      } else {
        console.error('🎯 [LOGINPAGE] No user in result despite success=true')
      }
    } catch (error: any) {
      console.error('🎯 [LOGINPAGE] Caught exception:', error)
      setError('An unexpected error occurred')
    } finally {
      console.log('🎯 [LOGINPAGE] Setting loading to false')
      setLoading(false)
    }
  }

  async function handleForgotPassword(e: React.FormEvent) {
    e.preventDefault()
    setError('')

    if (!email) {
      setError('Please enter your email address')
      return
    }

    try {
      setLoading(true)

      // Import requestPasswordReset dynamically
      const { requestPasswordReset } = await import('@/lib/authService')
      const result = await requestPasswordReset(email)

      if (result.success) {
        setResetSent(true)
      } else {
        setError(result.error || 'Failed to send reset email')
      }
    } catch (error: any) {
      console.error('Password reset error:', error)
      setError('An unexpected error occurred')
    } finally {
      setLoading(false)
    }
  }

  if (resetMode) {
    return (
      <div
        className="min-h-screen flex items-center justify-center p-4 relative"
        style={{
          backgroundImage: 'url(/images/login-background.jpg)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      >
        {/* Dark overlay for better readability */}
        <div className="absolute inset-0 bg-black/50"></div>

        <Card className="w-full max-w-md shadow-xl border relative z-10">
          <CardHeader className="space-y-1">
            <div className="flex items-center justify-center mb-4">
              <span className="text-4xl font-bold">
                <span>Faith</span><span className="text-orange-500">Y</span><span>oke</span>
              </span>
            </div>
            <CardTitle className="text-2xl text-center">Reset Password</CardTitle>
            <CardDescription className="text-center">
              {resetSent
                ? 'Check your email for reset instructions'
                : 'Enter your email to receive reset instructions'}
            </CardDescription>
          </CardHeader>

          <CardContent className="pt-6">
            {resetSent ? (
              <div className="space-y-4">
                <Alert className="bg-green-50 border-green-200">
                  <AlertDescription className="text-green-800">
                    If an account exists with that email, you'll receive password reset instructions shortly.
                  </AlertDescription>
                </Alert>

                <Button
                  type="button"
                  onClick={() => {
                    setResetMode(false)
                    setResetSent(false)
                  }}
                  className="w-full"
                >
                  Back to Login
                </Button>
              </div>
            ) : (
              <form onSubmit={handleForgotPassword} className="space-y-4">
                {error && (
                  <Alert className="bg-red-50 border-red-200">
                    <AlertCircle className="h-4 w-4 text-red-600" />
                    <AlertDescription className="text-red-800">{error}</AlertDescription>
                  </Alert>
                )}

                <div className="space-y-2">
                  <Label htmlFor="reset-email">Email Address</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                    <Input
                      id="reset-email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="pastor@church.com"
                      className="pl-10"
                      required
                      disabled={loading}
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      'Send Reset Link'
                    )}
                  </Button>

                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => {
                      setResetMode(false)
                      setError('')
                    }}
                    className="w-full"
                  >
                    Back to Login
                  </Button>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      </div>
    )
  }

  return (
    <div
      className="min-h-screen flex items-center justify-center p-4 relative"
      style={{
        backgroundImage: 'url(/images/login-background.jpg)',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      {/* Dark overlay for better readability */}
      <div className="absolute inset-0 bg-black/50"></div>

      <Card className="w-full max-w-md shadow-xl border relative z-10">
        <CardHeader className="space-y-1">
          <div className="flex items-center justify-center mb-4">
            <span className="text-4xl font-bold">
              <span>Faith</span><span className="text-orange-500">Y</span><span>oke</span>
            </span>
          </div>
          <CardDescription className="text-center">
            Sign in to manage your church
          </CardDescription>
        </CardHeader>

        <CardContent className="pt-6">
          <form onSubmit={handleLogin} className="space-y-4">
            {error && (
              <Alert className="bg-red-50 border-red-200">
                <AlertCircle className="h-4 w-4 text-red-600" />
                <AlertDescription className="text-red-800">{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="email">Email Address</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="pastor@church.com"
                  className="pl-10"
                  required
                  disabled={loading}
                  autoComplete="email"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Lock className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="pl-10 pr-10"
                  required
                  disabled={loading}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-3 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <input
                  id="remember"
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="rounded border-gray-300"
                  disabled={loading}
                />
                <Label htmlFor="remember" className="text-sm font-normal cursor-pointer">
                  Remember me (30 days)
                </Label>
              </div>

              <button
                type="button"
                onClick={() => setResetMode(true)}
                className="text-sm text-primary hover:text-primary/80 font-medium"
              >
                Forgot password?
              </button>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Signing in...
                </>
              ) : (
                'Sign In'
              )}
            </Button>
          </form>

          <div className="mt-6 text-center text-sm text-muted-foreground">
            <p>Protected with end-to-end encryption</p>
            <p className="text-xs mt-1">
              Supports 2FA via SMS (mNotify) and Authenticator apps
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
